'use client'

import React, { useEffect, useRef, useState } from 'react'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/utilities/ui'

export function StickyFilterBar({
  label,
  children,
  className,
}: {
  children: React.ReactNode
  label: React.ReactNode
  className?: string
}) {
  const labelRef = useRef<HTMLDivElement>(null)

  const [scrollAreaWidth, setScrollAreaWidth] = useState(0)
  useEffect(() => {
    const calcScrollAreaWidth = () => {
      const clientWidth = document.documentElement.clientWidth

      if (clientWidth < 768) {
        setScrollAreaWidth(clientWidth)
        return
      }

      const labelEl = labelRef.current

      if (labelEl) {
        const filterRect = labelEl.getBoundingClientRect()
        const width = clientWidth - filterRect.right - 30 - 4

        // Set the width of the scroll area to fit remaining space
        setScrollAreaWidth(width)
      }
    }

    // Set width initially and on window resize
    calcScrollAreaWidth()
    window.addEventListener('resize', calcScrollAreaWidth)

    // Clean up
    return () => {
      window.removeEventListener('resize', calcScrollAreaWidth)
    }
  }, [])

  return (
    <div className={cn('overflow-hidden sticky top-[80px] mb-[10px] z-20', className)}>
      <div
        className={cn(
          'container flex',
          'justify-between mx-auto items-center gap-[10px] flex-col',
          'md:flex-row md:pl-4 md:gap-[30px]',
        )}
      >
        <div className="flex-none" ref={labelRef}>
          {label}
        </div>
        <div className={cn('flex-1 overflow-x-hidden md:overflow-x-visible')}>
          <ScrollArea
            className="w-full overflow-x-hidden"
            style={{
              width: scrollAreaWidth ? `${scrollAreaWidth}px` : '',
            }}
          >
            {children}
          </ScrollArea>
        </div>
      </div>
    </div>
  )
}
